import { Component, OnInit, ViewChild, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Table } from 'primeng/table';
import { StatusPipe } from '@src/app/shared/pipes/status.pipe';
import { InventoryItemState } from '@app/shared/enums/InventoryItemState.enum';
import { InventoryItemStatePipe } from '@src/app/shared/pipes/inventoryItemState.pipe';
import { ConfirmationService } from 'primeng/api';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { InventoriesService, RemoveInventoryItemListRequest, InventoryItemListRequest, ReviewInventoryItemRequest, InventoryDetailsResponse, BrandsService } from '@src/app/shared/backend/clients';
import { FriendlyBooleanPipe } from '@src/app/shared/pipes/friendlyBoolean.pipe';
import { spacesValidator } from '@src/app/shared/validators/trimSpaces.validator';
import { PermissionService } from '@src/app/shared/services/permission.service';
import { Actions } from '@src/app/shared/backend/auth/Action';
import { FilterOperator } from '@src/app/shared/enums/FilterOperator.enum';
import { SessionService } from '@src/app/shared/services/session.service';
import { BaseViewComponent } from '@src/app/shared/components/base-list/base-view.component';
import { InventoryMultiScannerItem, MultiScannerComponent } from '@src/app/shared/components/multi-scanner/multi-scanner.component';
import { InvalidateInventoryRequest } from '@src/app/shared/backend/clients/model/invalidateInventoryRequest';
import { DialogReasonComponent } from '@src/app/shared/components/dialog-reason/dialog-reason.component';

@Component({
    selector: 'app-view-inventory',
    templateUrl: './view-inventory.component.html',
    styleUrl: './view-inventory.component.scss',
    host: {
        class: 'app-view-inventory'
    },
    providers: [StatusPipe, ConfirmationService, FriendlyBooleanPipe, InventoryItemStatePipe],
    standalone: false
})
export class ViewInventoryComponent extends BaseViewComponent implements OnInit, OnDestroy {

  private readonly destroyedComponent$ = new Subject<void>();

  @ViewChild('dialog') dialog: DialogReasonComponent | undefined;

  delete: boolean = false;
  allowModifyInventory: boolean = false;
  allowInvalidateInventory: boolean = false;
  allowViewRetailer: boolean = false;
  allowViewUser: boolean = false;

  brandsTotalRecords: number = 0;
  hasOnlyOneActiveBrand: boolean = false;

  inventoryId: number = 0;
  inventoryData: InventoryDetailsResponse = {
    id: 0,
    name: '',
    totalItems: 0,
    brandId: 0,
    retailerId: 0,
    retailerName: '',
    isLast: false,
    createdOn: '',
    updatedOn: '',
    createdById: '',
    updatedById: '',
    createdByFullName: '',
    updatedByFullName: '',
    brandName: '',
    isInvalid: false,
    invalidationReason: ''
  };

  inventoryItemState = InventoryItemState;

  displayAddItemsModalbox: boolean = false;
  displayInventoryItemDetailsModalbox: boolean = false;
  displayRemoveItemConfirmationModalbox: boolean = false;
  displayUnsavedScannerItemsModalbox: boolean = false;
  displayInvalidateInventoryModalbox: boolean = false;

  scannerMounted: boolean = false;
  scannerInitialItems: string[] = [];

  selectedItemId: number | undefined;
  companyHasCpo: boolean = false;

  inventoryItemId: number = 0;
  itemsList: any;
  isResultsEmpty: boolean = true;
  inMemoryPagedResults: any[] = [];
  inMemoryTotalRecords: number = 0;
  inMemoryPaginator = { first: 0, rows: 5 };

  filterOperator = FilterOperator;

  @ViewChild('dtInventoryContentTable') table: Table | undefined;
  @ViewChild('multiScanner') multiScanner?: MultiScannerComponent;

  // local pagination
  paginatorFirst = 0;
  paginatorRows = 10;

  addItemsFormGroup: FormGroup = this.fb.group({
    inventoryItems: [[], Validators.required],
    serialNumber: ['', spacesValidator()]
  });

  public invalidateInventoryForm: FormGroup = this.fb.group({
    reason: ['', [Validators.required, Validators.maxLength(50)]],
  });

  constructor(
    private permissionService: PermissionService,
    private router: Router,
    private route: ActivatedRoute,
    private session: SessionService,
    private inventoriesService: InventoriesService,
    private brandsService: BrandsService,
    private changeDetector: ChangeDetectorRef,
    private fb: FormBuilder) { super(); }

  ngOnInit() {
    this.init();
  }

  private async init(): Promise<void> {
    this.route.params
      .pipe(takeUntil(this.destroyedComponent$))
      .subscribe(async params => {
        this.inventoryId = Number(params['inventoryId']);

        if (this.inventoryId > 0) {
            await Promise.all([
            this.getBrands()
          ]);

          await this.get();
        }
      });

    this.session.currentCompanyData$
      .pipe(takeUntil(this.destroyedComponent$))
      .subscribe(company => {
        this.companyHasCpo = !!company?.hasCpo;
      });

    this.delete = await this.permissionService.hasActionPermissions(Actions.delete);
    this.allowViewRetailer = await this.permissionService.hasActionPermissions(Actions.view_retailers);
    this.allowViewUser = await this.permissionService.hasActionPermissions(Actions.view_users);

    this.addItemsFormGroup.get('serialNumber')?.valueChanges
      .pipe(takeUntil(this.destroyedComponent$))
      .subscribe(value => {
        const rawValue = String(value ?? '');
        const trimmedValue = rawValue.trim();

        if (rawValue !== trimmedValue) {
          this.addItemsFormGroup.get('serialNumber')?.setValue(trimmedValue, { emitEvent: false });
        }
      });
  }

  override async get(): Promise<void> {
    try {
      this.inventoryData = await firstValueFrom(
        this.inventoriesService.apiInventoriesDetailsIdGet(this.inventoryId)
      );
      this.allowModifyInventory = await this.permissionService.hasActionPermissions(Actions.modify_inventories);
      this.allowInvalidateInventory = await this.permissionService.hasActionPermissions(Actions.invalidate_inventory);
    } catch (error) {
      console.error('Error fetching inventory data:', error);
    } finally {
      this.changeDetector.detectChanges();
    }
  }

  get totalRecords(): number {
    return this.inventoryData?.inventoryItems?.length ?? 0;
  }

  get paginatedItems(): any[] {
    const items = this.inventoryData?.inventoryItems ?? [];
    const startIndex = this.paginatorFirst;
    const endIndex = startIndex + this.paginatorRows;

    return items.slice(startIndex, endIndex);
  }

  get paginatorPageNumber(): number {
    const rows = this.paginatorRows || 1;

    // convert local 0-based item offset to the 1-based paginator expected page number
    return Math.floor((this.paginatorFirst ?? 0) / rows) + 1;
  }

  onPageChange(event: any): void {
    this.paginatorFirst = event.first;
    this.paginatorRows = event.rows;
  }

  addInventoryItems(): void {
    this.scannerInitialItems = this.getSelectedSerialNumbers();
    this.displayAddItemsModalbox = true;
  }

  onScannerDialogShow(): void {
    this.scannerMounted = true;
    this.changeDetector.detectChanges();
  }

  async onScannerDialogHide(): Promise<void> {
    await this.multiScanner?.stopScanner();
    this.scannerMounted = false;
  }

  async onSubmitAddedItems(): Promise<void> {
    if (!this.addItemsFormGroup.valid) return;

    const serialNumbersObj = this.addItemsFormGroup.controls['inventoryItems'].value ?? [];
    const serialNumberList = serialNumbersObj.map((item: { serialNumber: string }) => item.serialNumber);

    const payload: InventoryItemListRequest = {
      itemList: serialNumberList
    };

    await firstValueFrom(this.inventoriesService.apiInventoriesIdItemsAddPut(this.inventoryId, payload));

    this.displayAddItemsModalbox = false;
    this.resetScannerDraft();
    this.resetInventoryItemsPaginator();

    await this.get();
  }

  async onCancelItemAdd(): Promise<void> {
    await this.multiScanner?.stopScanner();

    this.displayAddItemsModalbox = false;
    this.resetScannerDraft();
  }

  async revalidateInventoryItems(): Promise<void> {
    try {
      this.inventoryData = await firstValueFrom(
        this.inventoriesService.apiInventoriesIdRevalidateGet(this.inventoryId)
      );
    } catch (error) {
      console.error('Error revalidating inventory items:', error);
    } finally {
      this.changeDetector.detectChanges();
    }

    await this.get();
  }

  invalidateInventory(): void {
    this.displayInvalidateInventoryModalbox = true;
  }

  async onSubmitInvalidateInventoryForm(): Promise<void> {
    try{
      if (this.invalidateInventoryForm.invalid) return;

      const payload: InvalidateInventoryRequest = {
            reason: this.invalidateInventoryForm.controls['reason'].value,
      };

      await firstValueFrom(this.inventoriesService.apiInventoriesIdInvalidatePatch(this.inventoryId, payload));
    } catch (error) {
      console.error('Error invalidating inventory items:', error);
    }
    finally{
      this.displayInvalidateInventoryModalbox = false;
      this.invalidateInventoryForm.reset(); 
      await this.get();
    }
  }

  goToEditInventory(): void {
    this.router.navigate([`/inventories/${this.inventoryId}/edit`]);
  }

  getSelectedItems(): any[] {
    return this.addItemsFormGroup.get('inventoryItems')?.value ?? [];
  }

  addToPayload(): void {
    const serialNumber = this.addItemsFormGroup.get('serialNumber')?.value?.trim();

    if (serialNumber && !this.getSelectedItems().some((item: any) => item.serialNumber === serialNumber)) {
      const selectedItems = [...this.getSelectedItems(), { serialNumber }];

      this.addItemsFormGroup.patchValue({ inventoryItems: selectedItems });
      this.addItemsFormGroup.get('serialNumber')?.reset();
    }
  }

  removeItemChip(item: any): void {
    const selectedItemsList = [...this.getSelectedItems()];
    const indexToRemove = selectedItemsList.indexOf(item);

    if (indexToRemove !== -1) {
      selectedItemsList.splice(indexToRemove, 1);
      this.addItemsFormGroup.patchValue({ inventoryItems: selectedItemsList });
    }
  }

  onRemoveItemFromInventory(event: any, id: number): void {
    this.selectedItemId = id;
    this.displayRemoveItemConfirmationModalbox = true;
  }

  get inventoryItemCount(): number {
    const inventoryItemsControl = this.addItemsFormGroup.get('inventoryItems');
    return inventoryItemsControl?.value?.length || 0;
  }

  onCancelItemRemoval(): void {
    this.displayRemoveItemConfirmationModalbox = false;
    this.selectedItemId = undefined;
  }

  async onConfirmItemRemoval(): Promise<void> {
    if (!this.selectedItemId) {
      this.displayRemoveItemConfirmationModalbox = false;
      this.selectedItemId = undefined;
      return;
    }

    try {
      const payload: RemoveInventoryItemListRequest = {
        itemListToRemove: [this.selectedItemId]
      };

      await firstValueFrom(
        this.inventoriesService.apiInventoriesIdItemsRemovePut(this.inventoryId, payload)
      );
    } catch (error) {
      console.error('Error removing inventory item:', error);
    } finally {
      this.displayRemoveItemConfirmationModalbox = false;
      this.selectedItemId = undefined;
      await this.get();
    }
  }

  async showDeleteDialog(): Promise<void> {
    const deleteCheckResponses = await firstValueFrom(this.inventoriesService.apiInventoriesDeleteCheckBatchPost([this.inventoryId]));

    this.dialog?.show(deleteCheckResponses);
  }

  async deleteInventory(): Promise<void> {
    await firstValueFrom(
      this.inventoriesService.apiInventoriesDeleteIdDelete(this.inventoryId)
    );
    this.router.navigate([`/inventories`]);
  }

  async viewInventoryItemDetails(event: any, serialNumber: string, inventoryItemId: number): Promise<void> {
    try {
      const itemsPage = await firstValueFrom(
        this.inventoriesService.apiInventoriesReviewCandidateIdSerialNumberGet(this.inventoryId, serialNumber)
      );

      this.itemsList = itemsPage.results || [];
      this.isResultsEmpty = this.itemsList.length === 0;
      this.resetInMemoryPaginatorAndSlice();

    } catch (error) {
      console.error('Error validating serial number', error);
      this.itemsList = [];
      this.isResultsEmpty = true;
      this.updateInMemoryPageSlice();
    }

    this.inventoryItemId = inventoryItemId;
    this.displayInventoryItemDetailsModalbox = true;
  }

  private updateInMemoryPageSlice(): void {
    const src = this.itemsList || [];

    this.inMemoryTotalRecords = src.length;

    const { first, rows } = this.inMemoryPaginator;

    this.inMemoryPagedResults = src.slice(first, first + rows);
    this.isResultsEmpty = this.inMemoryTotalRecords === 0;
  }

  private resetInMemoryPaginatorAndSlice(): void {
    this.inMemoryPaginator.first = 0;
    this.updateInMemoryPageSlice();
  }

  private resetInventoryItemsPaginator(): void {
    this.paginatorFirst = 0;
  }

  onInMemoryPageChange(event: any): void {
    this.inMemoryPaginator.first = event.first ?? 0;
    this.inMemoryPaginator.rows = event.rows ?? this.inMemoryPaginator.rows;

    this.updateInMemoryPageSlice();
  }

  async onSelectItem(itemId: number): Promise<void> {
    const payload: ReviewInventoryItemRequest = {
      inventoryItemId: this.inventoryItemId,
      itemId
    };

    try {
      await firstValueFrom(this.inventoriesService.apiInventoriesReviewPut(payload));
    } catch (error) {
      console.error('Error reviewing inventory item', error);
    } finally {
      this.inventoryItemId = 0;
      this.displayInventoryItemDetailsModalbox = false;
      await this.get();
    }
  }

  getSelectedSerialNumbers(): string[] {
    return this.getSelectedItems().map((item: any) => item.serialNumber);
  }

  onScannerItemsChange(items: InventoryMultiScannerItem[]): void {
    this.addItemsFormGroup.patchValue({
      inventoryItems: items.map(item => ({
        serialNumber: item.serialNumber
      }))
    });

    this.addItemsFormGroup.get('inventoryItems')?.markAsDirty();
    this.addItemsFormGroup.get('inventoryItems')?.updateValueAndValidity();
  }

  async onScannerFinish(items: InventoryMultiScannerItem[]): Promise<void> {
    await this.multiScanner?.stopScanner();

    this.onScannerItemsChange(items);

    if (!items.length) {
      this.displayAddItemsModalbox = false;
      this.resetScannerDraft();
      return;
    }

    await this.onSubmitAddedItems();
  }

  async onScannerCancel(): Promise<void> {
    await this.multiScanner?.stopScanner();

    this.displayAddItemsModalbox = false;
    this.resetScannerDraft();
  }

  onScannerError(error: any): void {
    console.error('Inventory scanner error:', error);
  }

  private resetScannerDraft(): void {
    this.scannerInitialItems = [];

    this.addItemsFormGroup.patchValue({
      inventoryItems: [],
      serialNumber: ''
    });

    this.addItemsFormGroup.markAsPristine();
    this.addItemsFormGroup.updateValueAndValidity();
  }

  requestScannerModalClose(): void {
    if (this.hasUnsavedScannerItems()) {
      this.displayAddItemsModalbox = false;

      setTimeout(() => {
        this.displayUnsavedScannerItemsModalbox = true;
      });

      return;
    }

    this.onScannerCancel();
  }

  async dismissUnsavedScannerItems(): Promise<void> {
    this.displayUnsavedScannerItemsModalbox = false;
    // await this.onScannerCancel();
  }

  async saveUnsavedScannerItems(): Promise<void> {
    this.displayUnsavedScannerItemsModalbox = false;

    // const scannedItems = this.multiScanner?.scannedItems ?? [];
    // await this.onScannerFinish(scannedItems);

    await this.onSubmitAddedItems();
  }

  private hasUnsavedScannerItems(): boolean {
    return this.getSelectedItems().length > 0;
  }

  private async getBrands(): Promise<void> {
    const payload = {
      pageNumber: 1,
      pageSize: 1,
      additionalFilters: [
        {
          propertyName: 'isActive',
          operator: FilterOperator.EQUALS,
          values: ['true']
        }
      ]
    };

    const brandsPage = await firstValueFrom(this.brandsService.apiBrandsFilterComboPost(payload));

    this.brandsTotalRecords = brandsPage.totalCount ?? 0;
    this.hasOnlyOneActiveBrand = this.brandsTotalRecords === 1;
  }

  ngOnDestroy(): void {
    this.destroyedComponent$.next();
    this.destroyedComponent$.complete();
  }

  onCancelInvalidateInventoryForm(): void {
    this.displayInvalidateInventoryModalbox = false;
    this.invalidateInventoryForm.reset();
  }
}
